/**
 * What makes a hub worth opening.
 *
 * Each hub is a landing page over a set of screens, so it opens when any one of
 * those screens does. Those screens are grants now -- `posting · read`, or a sub
 * of the situation posting -- rather than permission names, so the lists below
 * are coordinates and the checks go through `util/access`.
 */

import { type AccessPolicy, canRead, canSeeAny, canSeeAnySituation, canSeeLiveStock, canSeeReports, canSeeSituation, canSeeStockTimeline, canTouch, canTraceStock, type GrantSpec, type SituationKind } from '../../util/access';

/** Postings whose `read` opens the People hub, plus the situations it shows;
 * the address book is a table. */
export const PEOPLE_HUB_POSTINGS = ['wallet'] as const;
export const PEOPLE_HUB_SITUATIONS: readonly SituationKind[] = [
  'client',
  'agent',
  'supplier',
];

export const CASH_HUB_POSTINGS = [
  'expense',
  'voucher',
  'deposit',
  'supply_payment',
] as const;

export const STOCK_HUB_POSTINGS = [
  'movement',
  'discard',
  'supply',
  'inventory',
  'conversion',
  'production',
] as const;

/** The two stock screens backed by standalone resource grants. */
export const STOCK_MENU_GRANTS: Record<'10.3' | '16.1', GrantSpec> = {
  '10.3': { resource: 'item', action: 'read' },
  '16.1': { resource: 'prod_bom', action: 'read' },
};

/** The stock hub also holds the three read-only stock views. */
const stockViews = (access: AccessPolicy | null | undefined) =>
  canTraceStock(access) || canSeeStockTimeline(access) || canSeeLiveStock(access);

export const PEOPLE_TABS = ['20', '5', '5.1', '5.2', '13', '14'];
export const CASH_TABS = ['21', '17', '18', '6', '7', '15'];
export const STOCK_TABS = ['22', '8', '8.1', '8.2', '8.3', '9', '9.1', '9.2', '10', '10.1', '10.2', '10.3', '11', '16', '16.1'];
export const REPORT_LIVE_TABS = [
  '2',
  '19',
  '3.1', '3.2', '3.3', '3.4',
  '4.1', '4.2', '4.3', '4.4', '4.5', '4.6',
  '15.1', '15.2', '15.3',
];

/** Live (non-printable) report screens, which read postings directly. */
export const LIVE_REPORT_POSTINGS = [
  'offer',
  'sale',
  'return',
  'payment',
  'supply_payment',
] as const;

export const canAccessPeopleHub = (access: AccessPolicy | null | undefined) =>
  PEOPLE_HUB_POSTINGS.some((p) => canTouch(access, p)) ||
  canRead(access, 'client_address') ||
  canSeeAnySituation(access, PEOPLE_HUB_SITUATIONS);

export const canAccessCashHub = (access: AccessPolicy | null | undefined) =>
  CASH_HUB_POSTINGS.some((p) => canTouch(access, p));

export const canAccessStockHub = (access: AccessPolicy | null | undefined) =>
  STOCK_HUB_POSTINGS.some((p) => canTouch(access, p)) ||
  stockViews(access) ||
  canSeeSituation(access, 'supply') ||
  canRead(access, 'item') ||
  canRead(access, 'prod_bom');

export const canAccessReportsHub = (access: AccessPolicy | null | undefined) =>
  canSeeReports(access) ||
  canSeeAny(access, LIVE_REPORT_POSTINGS) ||
  canSeeSituation(access, 'invoice');

export type HubTarget = {
  path: string;
  titleKey: string;
};

export const HUB_TARGETS = {
  people: { path: '/people', titleKey: 'nav:tabs.people' },
  cash: { path: '/cash', titleKey: 'nav:tabs.cash' },
  stock: { path: '/stock', titleKey: 'nav:tabs.stock' },
  reports: { path: '/reports', titleKey: 'nav:tabs.reports' },
} as const;

type PathHubRule = { prefix: string; hub: keyof typeof HUB_TARGETS };

const PATH_TO_HUB: PathHubRule[] = [
  { prefix: '/clients/wallets/transactions', hub: 'people' },
  { prefix: '/clients/wallets', hub: 'people' },
  { prefix: '/clients', hub: 'people' },
  { prefix: '/agents', hub: 'people' },
  { prefix: '/suppliers', hub: 'people' },
  { prefix: '/expenses', hub: 'cash' },
  { prefix: '/vouchers', hub: 'cash' },
  { prefix: '/deposits/pending', hub: 'cash' },
  { prefix: '/deposits', hub: 'cash' },
  { prefix: '/supply-payments/mode', hub: 'reports' },
  { prefix: '/supply-payments/user', hub: 'reports' },
  { prefix: '/supply-payments/depot', hub: 'reports' },
  { prefix: '/supply-payments', hub: 'cash' },
  { prefix: '/stock/movement', hub: 'stock' },
  { prefix: '/stock/supply', hub: 'stock' },
  { prefix: '/stock/inventory', hub: 'stock' },
  { prefix: '/stock/conversion', hub: 'stock' },
  { prefix: '/stock/production', hub: 'stock' },
  { prefix: '/offers', hub: 'reports' },
  { prefix: '/invoice/situation', hub: 'reports' },
  { prefix: '/sales/item', hub: 'reports' },
  { prefix: '/sales/family', hub: 'reports' },
  { prefix: '/sales/category', hub: 'reports' },
  { prefix: '/sales/depot', hub: 'reports' },
  { prefix: '/sales/sector', hub: 'reports' },
  { prefix: '/sales/user', hub: 'reports' },
  { prefix: '/payments/mode', hub: 'reports' },
  { prefix: '/payments/type', hub: 'reports' },
  { prefix: '/payments/user', hub: 'reports' },
  { prefix: '/payments/depot', hub: 'reports' },
];
PATH_TO_HUB.sort((left, right) => right.prefix.length - left.prefix.length);

export const resolveHubForPath = (pathname: string): HubTarget | null => {
  const match = PATH_TO_HUB.find((rule) =>
    pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`),
  );
  return match ? HUB_TARGETS[match.hub] : null;
};
